import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Users, FileText, MessageSquare, TrendingUp, LogOut, Brain,
  Building, Calendar, Euro, Mail, Phone, RefreshCw, Plus,
  ChevronDown, ChevronUp, QrCode, Copy, CheckCircle, Clock, AlertCircle,
  Send, Gamepad2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [responses, setResponses] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  
  // Game participants
  const [gameParticipants, setGameParticipants] = useState([]);
  const [gameStats, setGameStats] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  
  // Company creation form
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [newCompany, setNewCompany] = useState({
    company_name: "",
    sector: "",
    description: "",
    employee_roles: ""
  });
  const [createdCompany, setCreatedCompany] = useState(null);

  const authHeader = sessionStorage.getItem("adminAuth");
  const adminEmail = sessionStorage.getItem("adminEmail");

  useEffect(() => {
    if (!authHeader) {
      navigate("/admin");
      return;
    }
    fetchAllData();
  }, [authHeader, navigate]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const headers = { Authorization: `Basic ${authHeader}` };
      
      const [statsRes, prospectsRes, quotesRes, messagesRes, responsesRes, gameParticipantsRes, gameStatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/prospects`, { headers }),
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/messages`, { headers }),
        axios.get(`${API}/admin/responses`, { headers }),
        axios.get(`${API}/admin/game-participants`, { headers }),
        axios.get(`${API}/admin/game-stats`, { headers })
      ]);

      setStats(statsRes.data);
      setProspects(prospectsRes.data);
      setQuotes(quotesRes.data);
      setMessages(messagesRes.data);
      setResponses(responsesRes.data);
      setGameParticipants(gameParticipantsRes.data);
      setGameStats(gameStatsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        navigate("/admin");
      }
      toast.error("Erreur de chargement");
    } finally {
      setIsLoading(false);
    }
  };

  const sendDailyReport = async () => {
    setSendingReport(true);
    try {
      const headers = { Authorization: `Basic ${authHeader}` };
      const response = await axios.post(`${API}/admin/send-daily-report`, {}, { headers });
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Send report error:", error);
      toast.error("Erreur lors de l'envoi du rapport");
    } finally {
      setSendingReport(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    sessionStorage.removeItem("adminEmail");
    navigate("/admin");
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/companies`, {
        company_name: newCompany.company_name,
        sector: newCompany.sector,
        description: newCompany.description,
        employee_roles: newCompany.employee_roles.split(",").map(r => r.trim()).filter(r => r)
      });
      
      setCreatedCompany(response.data);
      toast.success("Entreprise créée !");
      setNewCompany({ company_name: "", sector: "", description: "", employee_roles: "" });
    } catch (error) {
      console.error("Create company error:", error);
      toast.error("Erreur lors de la création");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const statusColors = {
    nouveau: "bg-blue-500/20 text-blue-400",
    "en attente": "bg-yellow-500/20 text-yellow-400",
    répondu: "bg-green-500/20 text-green-400",
    envoyé: "bg-green-500/20 text-green-400"
  };

  const tabs = [
    { id: "overview", label: "Vue d'ensemble", icon: <TrendingUp className="w-4 h-4" /> },
    { id: "prospects", label: "Prospects", icon: <Users className="w-4 h-4" />, count: prospects.length },
    { id: "quotes", label: "Devis", icon: <FileText className="w-4 h-4" />, count: quotes.length },
    { id: "messages", label: "Messages", icon: <MessageSquare className="w-4 h-4" />, count: messages.length },
    { id: "game", label: "Jeu IA", icon: <QrCode className="w-4 h-4" /> }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#007AFF]/30 border-t-[#007AFF] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0F0F10] border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#007AFF] flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold">Admin Académie IA</h1>
              <p className="text-white/50 text-xs">{adminEmail}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={fetchAllData}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
              title="Rafraîchir"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-[#007AFF] text-white"
                  : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? "bg-white/20" : "bg-white/10"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#007AFF]" />
                  <span className="text-white/60 text-sm">Prospects</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats.prospects}</p>
              </div>
              
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-green-500" />
                  <span className="text-white/60 text-sm">Devis envoyés</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats.quotes}</p>
              </div>
              
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-2">
                  <MessageSquare className="w-5 h-5 text-amber-500" />
                  <span className="text-white/60 text-sm">Messages</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats.messages}</p>
              </div>
              
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-purple-500" />
                  <span className="text-white/60 text-sm">Personnes formées</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats.trained_count?.toLocaleString()}</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#007AFF]" />
                  Derniers prospects
                </h3>
                <div className="space-y-3">
                  {prospects.slice(0, 5).map(prospect => (
                    <div key={prospect.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white font-medium text-sm">{prospect.company_name}</p>
                        <p className="text-white/50 text-xs">{prospect.contact_name}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${statusColors[prospect.status] || statusColors.nouveau}`}>
                        {prospect.status}
                      </span>
                    </div>
                  ))}
                  {prospects.length === 0 && (
                    <p className="text-white/40 text-sm text-center py-4">Aucun prospect</p>
                  )}
                </div>
              </div>

              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-amber-500" />
                  Derniers messages
                </h3>
                <div className="space-y-3">
                  {messages.slice(0, 5).map(msg => (
                    <div key={msg.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white font-medium text-sm">{msg.name}</p>
                        <p className="text-white/50 text-xs truncate max-w-[200px]">{msg.message}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${statusColors[msg.status] || statusColors.nouveau}`}>
                        {msg.status}
                      </span>
                    </div>
                  ))}
                  {messages.length === 0 && (
                    <p className="text-white/40 text-sm text-center py-4">Aucun message</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Prospects Tab */}
        {activeTab === "prospects" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {prospects.map(prospect => (
              <div key={prospect.id} className="glass-card overflow-hidden">
                <button
                  onClick={() => toggleExpand(prospect.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center">
                      <Building className="w-5 h-5 text-[#007AFF]" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-medium">{prospect.company_name}</p>
                      <p className="text-white/50 text-sm">{prospect.contact_name} • {prospect.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${statusColors[prospect.status] || statusColors.nouveau}`}>
                      {prospect.status}
                    </span>
                    {expandedItems[prospect.id] ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                  </div>
                </button>
                
                {expandedItems[prospect.id] && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-4">
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-white/50 mb-1">Contact</p>
                        <p className="text-white flex items-center gap-2"><Mail className="w-4 h-4" /> {prospect.email}</p>
                        {prospect.phone && <p className="text-white flex items-center gap-2 mt-1"><Phone className="w-4 h-4" /> {prospect.phone}</p>}
                      </div>
                      <div>
                        <p className="text-white/50 mb-1">Entreprise</p>
                        <p className="text-white">{prospect.company_size} salariés</p>
                        <p className="text-white/70">{prospect.sector}</p>
                      </div>
                      <div>
                        <p className="text-white/50 mb-1">Formation</p>
                        <p className="text-white">{prospect.training_format}</p>
                        <p className="text-white/70">{prospect.participant_count} participants</p>
                      </div>
                    </div>
                    {prospect.specific_needs && (
                      <div className="mt-4">
                        <p className="text-white/50 text-sm mb-1">Besoins spécifiques</p>
                        <p className="text-white/80 text-sm">{prospect.specific_needs}</p>
                      </div>
                    )}
                    <p className="text-white/40 text-xs mt-4">Créé le {formatDate(prospect.created_at)}</p>
                  </div>
                )}
              </div>
            ))}
            {prospects.length === 0 && (
              <div className="glass-card p-12 text-center">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">Aucun prospect pour le moment</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Quotes Tab */}
        {activeTab === "quotes" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {quotes.map(quote => (
              <div key={quote.id} className="glass-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{quote.quote_number}</p>
                      <p className="text-white/50 text-sm">{quote.company_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold text-lg">{quote.total_ttc?.toFixed(2)} € TTC</p>
                    <p className="text-white/50 text-sm">{quote.total_ht?.toFixed(2)} € HT</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">{quote.training_format} • {quote.participant_count} pers. • {quote.days_count} jour(s)</span>
                  <span className="text-white/40">{formatDate(quote.created_at)}</span>
                </div>
              </div>
            ))}
            {quotes.length === 0 && (
              <div className="glass-card p-12 text-center">
                <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">Aucun devis pour le moment</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Messages Tab */}
        {activeTab === "messages" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {messages.map(msg => {
              const response = responses.find(r => r.original_message_id === msg.id);
              return (
                <div key={msg.id} className="glass-card overflow-hidden">
                  <button
                    onClick={() => toggleExpand(msg.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        msg.status === "répondu" ? "bg-green-500/10" : "bg-amber-500/10"
                      }`}>
                        {msg.status === "répondu" ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-500" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium">{msg.name}</p>
                        <p className="text-white/50 text-sm">{msg.subject || "Demande d'information"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${statusColors[msg.status] || statusColors.nouveau}`}>
                        {msg.status}
                      </span>
                      {expandedItems[msg.id] ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                    </div>
                  </button>
                  
                  {expandedItems[msg.id] && (
                    <div className="px-4 pb-4 border-t border-white/5 pt-4 space-y-4">
                      <div>
                        <p className="text-white/50 text-sm mb-2">Message original</p>
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-white/80 text-sm">{msg.message}</p>
                          <p className="text-white/40 text-xs mt-2">{msg.email} • {formatDate(msg.created_at)}</p>
                        </div>
                      </div>
                      
                      {response && (
                        <div>
                          <p className="text-white/50 text-sm mb-2 flex items-center gap-2">
                            <Brain className="w-4 h-4 text-[#007AFF]" />
                            Réponse de Lucie-IA
                          </p>
                          <div className="bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-lg p-3">
                            <p className="text-white/80 text-sm whitespace-pre-wrap">{response.response_text}</p>
                            <p className="text-white/40 text-xs mt-2">
                              {response.sent ? "✓ Envoyé" : "⏳ En attente d'envoi"} • {formatDate(response.created_at)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {messages.length === 0 && (
              <div className="glass-card p-12 text-center">
                <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">Aucun message pour le moment</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Game Tab */}
        {activeTab === "game" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Game Stats & Report */}
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <div className="glass-card p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center">
                    <Gamepad2 className="w-5 h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Total participants</p>
                    <p className="text-white text-2xl font-bold">{gameStats?.total_participants || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-card p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Aujourd'hui</p>
                    <p className="text-white text-2xl font-bold">{gameStats?.today_participants || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-card p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Emails envoyés</p>
                    <p className="text-white text-2xl font-bold">{gameStats?.emails_sent || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-card p-4">
                <button
                  onClick={sendDailyReport}
                  disabled={sendingReport}
                  className="w-full h-full flex flex-col items-center justify-center gap-2 text-center hover:bg-white/5 rounded-lg transition-colors disabled:opacity-50"
                  data-testid="send-daily-report"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    {sendingReport ? (
                      <RefreshCw className="w-5 h-5 text-amber-500 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  <span className="text-white/70 text-xs">
                    {sendingReport ? "Envoi..." : "Envoyer rapport quotidien"}
                  </span>
                </button>
              </div>
            </div>

            {/* Participants List */}
            <div className="glass-card p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#007AFF]" />
                Participants récents ({gameParticipants.length})
              </h3>
              
              {gameParticipants.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {gameParticipants.slice(0, 20).map((p, idx) => (
                    <div key={idx} className="flex items-start justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{p.prenom}</p>
                          {p.welcome_email_sent && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                              Email envoyé
                            </span>
                          )}
                        </div>
                        <p className="text-white/60 text-sm">{p.email}</p>
                        <p className="text-white/40 text-xs">{p.fonction}</p>
                        {p.request_text && (
                          <p className="text-white/50 text-xs mt-1 truncate max-w-md" title={p.request_text}>
                            "{p.request_text.substring(0, 80)}..."
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs text-white/40">
                        {p.created_at && new Date(p.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Gamepad2 className="w-12 h-12 text-white/10 mx-auto mb-3" />
                  <p className="text-white/40">Aucun participant pour le moment</p>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Create Company */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#007AFF]" />
                  Créer un code entreprise
                </h3>
                
                <form onSubmit={handleCreateCompany} className="space-y-4">
                  <div>
                    <Label htmlFor="company_name">Nom de l'entreprise</Label>
                    <Input
                      id="company_name"
                      value={newCompany.company_name}
                      onChange={(e) => setNewCompany({ ...newCompany, company_name: e.target.value })}
                      placeholder="Ex: Galile"
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sector">Secteur</Label>
                    <Input
                      id="sector"
                      value={newCompany.sector}
                      onChange={(e) => setNewCompany({ ...newCompany, sector: e.target.value })}
                      placeholder="Ex: Services aux entreprises"
                      className="mt-1"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={newCompany.description}
                      onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                      placeholder="Décrivez brièvement l'entreprise..."
                      className="mt-1"
                      rows={3}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="roles">Postes types (séparés par des virgules)</Label>
                    <Input
                      id="roles"
                      value={newCompany.employee_roles}
                      onChange={(e) => setNewCompany({ ...newCompany, employee_roles: e.target.value })}
                      placeholder="Ex: Commercial, RH, Directeur"
                      className="mt-1"
                    />
                  </div>
                  
                  <button type="submit" className="btn-primary w-full">
                    Créer le code
                  </button>
                </form>
              </div>

              {/* Generated Code */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-green-500" />
                  Code généré
                </h3>
                
                {createdCompany ? (
                  <div className="text-center">
                    <p className="text-white/60 text-sm mb-4">
                      Code pour <strong className="text-white">{createdCompany.company_name}</strong>
                    </p>
                    
                    <div className="bg-white/10 rounded-xl p-6 mb-4">
                      <p className="text-4xl font-mono font-bold text-[#007AFF] tracking-widest">
                        {createdCompany.code}
                      </p>
                    </div>
                    
                    <p className="text-white/50 text-sm mb-4">
                      Les participants utilisent ce code sur :<br />
                      <code className="text-[#007AFF]">/jeu</code>
                    </p>
                    
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdCompany.code);
                        toast.success("Code copié !");
                      }}
                      className="btn-secondary flex items-center gap-2 mx-auto"
                    >
                      <Copy className="w-4 h-4" />
                      Copier le code
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <QrCode className="w-16 h-16 text-white/10 mx-auto mb-4" />
                    <p className="text-white/40">Créez une entreprise pour générer un code</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Instructions */}
            <div className="glass-card p-6 mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">📖 Mode d'emploi</h3>
              <ol className="space-y-3 text-white/70 text-sm">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#007AFF]/20 text-[#007AFF] flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  Créez un code entreprise pour votre client (formulaire ci-dessus)
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#007AFF]/20 text-[#007AFF] flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  Partagez le code aux participants (ex: GALILE01)
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#007AFF]/20 text-[#007AFF] flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  Les participants scannent le QR ou vont sur <code className="bg-white/10 px-2 py-0.5 rounded">/jeu</code>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#007AFF]/20 text-[#007AFF] flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                  Ils entrent le code, leur prénom et poste pour découvrir leurs apps IA personnalisées
                </li>
              </ol>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
